import { cn } from "@/lib/cn";
import { ChartLegend, type LegendItem } from "./ChartLegend";
import { ChartTooltip } from "./ChartTooltip";
import { labelIndices, niceScale, useChartWidth, useHoverIndex } from "./chart-utils";

export interface ColumnSeries {
  key: string;
  label: string;
  /** A `chart-tone--*` suffix. */
  tone: string;
}

export interface ColumnDatum {
  key: string;
  label: string;
  title?: string;
  values: Record<string, number>;
}

/**
 * Stacked columns with the total printed above each one.
 *
 * Stacked rather than grouped because the two questions a stack answers are the
 * two being asked: the column's height is "how much are we finding this week"
 * and the segments are "how much of it was serious". Grouped bars answer the
 * second and leave the first as mental arithmetic across three bars.
 *
 * `series` is drawn bottom-up in the order given, so pass it in the order the
 * reader should scan - most serious at the top of the stack, where the eye lands
 * and where a small segment is not squashed against the axis.
 *
 * The total is printed rather than left to the tooltip. It is the headline
 * figure, and a number that takes a hover to read is a number nobody reads on a
 * dashboard they open forty times a week.
 */
export function ColumnChart({
  data,
  series,
  height = 236,
  totalNoun,
  className,
}: {
  data: ColumnDatum[];
  series: ColumnSeries[];
  height?: number;
  /** What the column total counts - "findings", "visits". Names the tooltip. */
  totalNoun?: string;
  className?: string;
}) {
  const [ref, width] = useChartWidth();
  const hover = useHoverIndex(data.length);

  const PAD_L = 38;
  const PAD_R = 12;
  // Taller than the trend chart's: the totals sit above the tallest column.
  const PAD_T = 22;
  const PAD_B = 26;

  const plotW = Math.max(1, width - PAD_L - PAD_R);
  const plotH = Math.max(1, height - PAD_T - PAD_B);
  const baseline = PAD_T + plotH;

  const totals = data.map((datum) =>
    series.reduce((sum, s) => sum + (datum.values[s.key] ?? 0), 0)
  );
  const scale = niceScale(Math.max(1, ...totals));

  const slot = plotW / Math.max(1, data.length);
  const columnWidth = Math.max(8, Math.min(46, slot * 0.56));
  const x = (index: number) => PAD_L + slot * (index + 0.5);
  const scaled = (value: number) => (Math.max(0, value) / scale.max) * plotH;

  const labelled = labelIndices(data.length, plotW);

  const hovered = data[hover.index];
  const hoverTop = baseline - scaled(totals[hover.index] ?? 0);
  const TIP_H = 34 + series.length * 20;
  const below = hoverTop < TIP_H + 28;

  const legend: LegendItem[] = series.map((s) => ({ key: s.key, label: s.label, tone: s.tone }));

  return (
    // See `TrendChart` - the ref has to be on an element that is always mounted.
    <div className={cn("chart", className)} ref={ref}>
      {data.length === 0 ? (
        <p className="chart-empty">Nothing recorded in this window yet.</p>
      ) : width === 0 ? (
        <div className="chart__pending" style={{ height }} />
      ) : (
        <>
          <svg
            viewBox={`0 0 ${width} ${height}`}
            width={width}
            height={height}
            className="chart__figure"
            role="img"
            aria-label={data
              .map(
                (datum, index) =>
                  `${datum.label}: ${totals[index]} total, ${series
                    .map((s) => `${s.label} ${datum.values[s.key] ?? 0}`)
                    .join(", ")}`
              )
              .join("; ")}
            onMouseLeave={() => hover.set(null)}
          >
            {scale.ticks.map((tick) => (
              <g key={tick}>
                <line
                  x1={PAD_L}
                  y1={baseline - scaled(tick)}
                  x2={width - PAD_R}
                  y2={baseline - scaled(tick)}
                  className={cn("chart__grid", tick === 0 && "chart__grid--base")}
                />
                <text
                  x={PAD_L - 8}
                  y={baseline - scaled(tick) + 3.5}
                  className="chart__y-tick tnum"
                >
                  {tick}
                </text>
              </g>
            ))}

            {data.map((datum, index) => {
              // Walked from the baseline up, so each segment sits on the one below
              // it rather than every segment being positioned from the top of a
              // total it would have to recompute.
              let cursor = baseline;
              const dim = hover.hovering && hover.index !== index;

              return (
                <g
                  key={datum.key}
                  onMouseEnter={() => hover.set(index)}
                  className={cn("chart__column", dim && "chart__column--dim")}
                >
                  {/* A full-height hit area, so the column responds anywhere in its
                  slot rather than only over the few pixels it is currently
                  tall. A one-event week is otherwise unhoverable. */}
                  <rect
                    x={x(index) - slot / 2}
                    y={PAD_T}
                    width={slot}
                    height={plotH}
                    className="chart__column-hit"
                  />

                  {series.map((s) => {
                    const value = datum.values[s.key] ?? 0;
                    if (value <= 0) return null;
                    const barHeight = Math.max(2, scaled(value));
                    cursor -= barHeight;
                    return (
                      <rect
                        key={s.key}
                        x={x(index) - columnWidth / 2}
                        y={cursor}
                        width={columnWidth}
                        height={barHeight}
                        className={cn("chart__seg", `chart-tone--${s.tone}`)}
                      />
                    );
                  })}

                  <text
                    x={x(index)}
                    y={baseline - scaled(totals[index] ?? 0) - 8}
                    className={cn("chart__total tnum", !totals[index] && "chart__total--zero")}
                  >
                    {totals[index]}
                  </text>
                </g>
              );
            })}

            <line
              x1={PAD_L}
              y1={baseline}
              x2={width - PAD_R}
              y2={baseline}
              className="chart__axis"
            />

            {data.map((datum, index) =>
              labelled.includes(index) ? (
                <text key={datum.key} x={x(index)} y={height - 8} className="chart__x-tick">
                  {datum.label}
                </text>
              ) : null
            )}
          </svg>

          <ChartTooltip
            open={hover.hovering}
            left={clamp((x(hover.index) / Math.max(1, width)) * 100, 18, 82)}
            top={below ? hoverTop + 18 : hoverTop - TIP_H - 14}
            below={below}
            title={`${hovered?.title ?? hovered?.label ?? ""} · ${totals[hover.index] ?? 0}${
              totalNoun ? ` ${totalNoun}` : ""
            }`}
            rows={series.map((s) => ({
              key: s.key,
              label: s.label,
              tone: s.tone,
              value: hovered?.values[s.key] ?? 0,
            }))}
          />

          <ChartLegend items={legend} />
        </>
      )}
    </div>
  );
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
